import { toYmdLocal } from "./utils";
import { isActiveEnrollment, isWaivedEnrollment, type EnrollmentLike } from "./enrollmentFilters";

/** Per employee x coverage type. Enrolled wins over waived when both rows exist. */
export type ElectionState = "enrolled" | "waived" | "none";

export interface FutureElection {
  kind: "enrolled" | "waived";
  /** YYYY-MM-DD */
  effectiveYmd: string;
}

/**
 * What one Employee Summary cell shows. `current` is the live snapshot (as of
 * today); `future` is an election that hasn't taken effect yet. When both exist
 * the cell shows `current` and mentions `future` in its tooltip.
 */
export interface CellElection {
  current: ElectionState;
  future: FutureElection | null;
}

/** True when a row's effective date is strictly after `todayYmd` (date-only compare). */
export function isFutureEffective(
  effectiveDate: string | Date | null | undefined,
  todayYmd: string = toYmdLocal(new Date())!,
): boolean {
  const eff = toYmdLocal(effectiveDate);
  return !!eff && eff > todayYmd;
}

/** YYYY-MM-DD → MM/DD/YYYY without going through Date (no timezone shift). */
export function formatYmdUs(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${m}/${d}/${y}`;
}

/**
 * Resolve the cell for one employee's rows of one coverage type (employee-level
 * rows only). Future rows (effective after today) never count as current:
 * pending/active non-waived → future election, waived → future waiver.
 * Terminated future rows are cancelled elections and are ignored, as before.
 * Among future rows, enrolled wins over waived, then the earliest date wins.
 */
export function resolveCellElection(
  rows: Array<EnrollmentLike>,
  todayYmd: string = toYmdLocal(new Date())!,
): CellElection {
  let current: ElectionState = "none";
  let future: FutureElection | null = null;

  for (const enr of rows) {
    const status = String(enr?.status || "").toLowerCase();
    const effYmd = toYmdLocal(enr?.effectiveDate);
    if (effYmd && effYmd > todayYmd) {
      if (status === "terminated") continue;
      const kind = enr.waivedCoverage || status === "waived" ? "waived" : "enrolled";
      const better =
        !future ||
        (kind === "enrolled" && future.kind === "waived") ||
        (kind === future.kind && effYmd < future.effectiveYmd);
      if (better) future = { kind, effectiveYmd: effYmd };
      continue;
    }
    // Same rules as the Enrollments tab for rows already in effect.
    if (isActiveEnrollment(enr)) current = "enrolled";
    else if (isWaivedEnrollment(enr) && current !== "enrolled") current = "waived";
  }

  return { current, future };
}

/** Tooltip text for a cell. */
export function cellElectionTooltip(cell: CellElection): string {
  const futureText = cell.future
    ? `${cell.future.kind === "waived" ? "Future waiver" : "Future election"} — effective ${formatYmdUs(cell.future.effectiveYmd)}`
    : null;
  if (cell.current === "none" && futureText) return futureText;
  const currentText = cell.current === "enrolled" ? "Enrolled" : cell.current === "waived" ? "Waived" : "No election";
  return futureText ? `${currentText} · ${futureText}` : currentText;
}
